import { Pause, Play, Reset } from "assets/icons/Common";
import classNames from "classnames";
import { AdjustableNumber } from "components/generic/AdjustableNumber";
import { Col, IconButtonInverted } from "components/primitives";
import { useEffect, useState } from "react";
import styled from "styled-components";
import { WidgetFC } from "types/Widget";
import useSound from "use-sound";

export interface InteractiveTimerState {
  setTime: number;
  currentTime: number;
}

// Radial Bar constants
const radius = 100;
const thickness = 12;

export const InteractiveTimer: WidgetFC<InteractiveTimerState> = ({
  state: { setTime, currentTime },
  setState,
  settings,
}) => {
  const { showWarn, sound: playSound, selectSound } = settings;
  // const [currentTime, setCurrentTime] = useState(state.setTime);
  const [playTimestamp, setPlayTimestamp] = useState<number>();
  const selectedSound = require("assets/sounds/" + (selectSound || "yoo-hoo.mp3"));
  const [play] = useSound(selectedSound);
  const [playAnim, setPlayAnim] = useState(false);

  const isCounting = Boolean(playTimestamp);

  useEffect(() => {
    const interval = setInterval(updateTimer, 100);
    return () => clearInterval(interval);
  });

  const updateTimer = () => {
    if (playTimestamp) {
      if (currentTime > 0) {
        const newCurrentTime = Math.max(setTime - (Date.now() - playTimestamp) / 1000, 0);
        setState({ currentTime: newCurrentTime });

        if (newCurrentTime <= 0) {
          setPlayTimestamp(undefined);
          if (playSound) {
            play();
          }
          setPlayAnim(true);
          setTimeout(() => setPlayAnim(false), 1000);
        }
      }
    }
  };

  const playPause = () => {
    if (playTimestamp) {
      //pause
      setPlayTimestamp(undefined);
    } else {
      //Play - adjust the timestamp by the difference between the set time and the current time if we're paused
      if (currentTime > 0) {
        setPlayTimestamp(Date.now() - (setTime - currentTime) * 1000);
      }
    }
  };

  const stop = () => {
    if (playTimestamp) {
      setPlayTimestamp(undefined);
    }
    setState({ currentTime: setTime });
  };

  // Currently not used, but might be in the future
  // const reset = () => {
  //   setPlayTimestamp(undefined);
  //   setState({ setTime: 0 });
  //   setCurrentTime(0);
  // };

  function onTimeAltered(newTime: number) {
    if (!playTimestamp) {
      setState({ setTime: Math.floor(newTime), currentTime: newTime });
    } else {
      setState({ currentTime: newTime });
    }
  }

  const getSVGCircle = (progress: number, colour: string) => {
    const progressRatio = progress * 100;
    const circumference = 2 * Math.PI * (radius - thickness);
    const strokeLength = (circumference / 100) * progressRatio;
    return (
      <circle
        stroke={colour}
        strokeWidth={thickness}
        strokeLinecap="butt"
        fill="none"
        cx={radius}
        cy={radius}
        r={radius - thickness}
        strokeDasharray={`${strokeLength},${circumference}`}
      />
    );
  };

  return (
    <OuterContainer>
      <ProgressRing viewBox={`0 0 ${radius * 2} ${radius * 2}`} width={"85%"} height={"85%"}>
        {getSVGCircle(1, "#DDE1E8")}
        {getSVGCircle(
          currentTime / setTime,
          currentTime < setTime * 0.2 && setTime !== 0 && showWarn
            ? "var(--danger)"
            : "var(--primary)",
        )}
      </ProgressRing>
      <TimerContainer>
        <AdjustableTimer
          value={currentTime}
          columns={[{ maxValue: 100 }, { text: ":" }, { maxValue: 60 }]}
          onValueIsNegative={() => 1}
          onValueIsOver={(val, max) => val % max}
          setValue={onTimeAltered}
          showButtons={!isCounting}
          minValue={1}
          textColor={currentTime === 0 ? "var(--danger)" : undefined}
          playAnim={playAnim}
        />
      </TimerContainer>
      <ControlsContainer>
        <ControlButton
          disabled={currentTime === setTime}
          withShadow={false}
          Icon={Reset}
          accentColor="var(--danger)"
          onClick={stop}
        />
        <ControlButton
          disabled={currentTime === 0}
          withShadow={false}
          Icon={playTimestamp ? Pause : Play}
          onClick={playPause}
        />
      </ControlsContainer>
    </OuterContainer>
  );
};

const OuterContainer = styled(Col)`
  padding: 5cqw;
  align-items: center;
  justify-content: center;
  height: 100%;
  position: relative;

  --animate-duration: 0.5s;
`;

const ControlsContainer = styled.div`
  height: 12cqh;
  width: 100%;
  position: absolute;
  bottom: 3cqh;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 15px;

  button {
    height: 100%;
  }
`;

const TimerContainer = styled.div`
  z-index: 1;
  position: absolute;
  left: 50%;
  top: 45%;
  transform: translate(-50%, -50%);
  width: 100%;
`;

const AdjustableTimer = styled(AdjustableNumber).attrs<{ playAnim: boolean }>((props) => ({
  className: classNames("animate__animated", props.playAnim && "animate__pulse"),
}))<{ playAnim: boolean }>``;

// The -90 in here defines the parent rotation of the circles.
const ProgressRing = styled.svg`
  transform-origin: center;
  position: absolute;
  transform: translate(-50%, -50%) rotate(-90deg);
  top: 45%;
  left: 50%;

  circle {
    transition: 0.1s linear;
  }
`;

const ControlButton = styled(IconButtonInverted)`
  aspect-ratio: 1;
  padding: 3cqw;
`;
